import * as React from 'react';
import styled, {css} from 'styled-components';
import {transparentize} from 'polished';
import {transition, visuallyHidden} from 'helpers/css';
import {Theme} from '../stores/AppStore';

export enum SwitchVariant {
  Default = 'default',
  Accent = 'accent'
}

export interface SwitchProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  variant?: SwitchVariant;
  label?: string;
}

export const Container = styled.label<Pick<SwitchProps, 'disabled'>>`
  cursor: ${({disabled}) => !disabled && 'pointer'};
  display: inline-flex;
`;

export const Input = styled.input<SwitchProps>`
  ${visuallyHidden};    
  
  &:focus-visible + {
    #switch-content #switch-box {
      box-shadow: 0 0 0 4px ${({theme}) => `
        ${transparentize(.5, theme.colors.primary)}
      `};
    }
  }
  
  &:checked + {
    #switch-content {
      #switch-box {
        background: ${({theme}) => theme.colors.primary};
        
        #switch-round {
          background: ${({theme}) => theme.colors.white};
          margin-left: calc(100% - 15px);
        }
      }
      
      #switch-label {
        color: ${({theme}) => theme.variables.color};
      }
    }
  }
`;

export const Content = styled.div`
  gap: 10px;
  align-items: center;
  grid-auto-flow: column;
  display: inline-grid;
`;

export const Box = styled.div<Pick<SwitchProps, 'variant'>>`
  width: 40px;
  padding: 2px;
  box-sizing: border-box;
  border-radius: 30px;
  
  ${({variant}) => {
    switch (variant) {
      case SwitchVariant.Default: return css`
        background: ${({theme}) => theme.variables.backgroundColor};
      `;
      case SwitchVariant.Accent: return css`
        background: ${({theme}) => theme.variables.accentBackgroundColor};
      `;
    }
  }};
  
  ${transition()};
`;

export const Round = styled.div`
  width: 15px;
  height: 15px;
  background: ${({theme}) => theme.name === Theme.Dark ? (
    transparentize(.8, theme.colors.white)
  ) : (
    transparentize(.6, theme.variables.color)
  )};
  border-radius: 50%;
  
  ${transition()};
`;

export const Label = styled.span`
  color: ${({theme}) => transparentize(.6, theme.variables.color)};
  font-weight: 500;
  user-select: none;
  
  ${transition()};
`;

/**
 * This is a component switch
 * @param {SwitchProps} props
 * @constructor
 */
function Switch(
    props: SwitchProps,
): React.ReactElement<SwitchProps> {
  return (
    <Container disabled={props.disabled}>
      <Input
        type="checkbox"
        {...props}
      />
      <Content id="switch-content">
        <Box id="switch-box" variant={props.variant}>
          <Round id="switch-round" />
        </Box>
        {props.label && (
          <Label id="switch-label">
            {props.label}
          </Label>
        )}
      </Content>
    </Container>
  );
}

Switch.defaultProps = {
  variant: SwitchVariant.Default,
};

export default Switch;
